import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import { Add, Edit, Delete, PictureAsPdf, Payment } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import DataTable from '../components/DataTable';
import PaymentModal from '../components/PaymentModal';

interface Invoice {
  id: string;
  doc_no: string;
  customer_name: string;
  work_title: string;
  issue_date: string;
  due_date: string;
  total: number;
  paid_amount: number;
  status: string;
  billing_round: number;
}

const statusColors: Record<string, string> = {
  draft: '#9e9e9e',
  issued: '#1976d2',
  paid: '#2e7d32',
  partial: '#f57f17',
  canceled: '#d32f2f',
};

const statusLabels: Record<string, string> = {
  draft: '下書き',
  issued: '発行済',
  paid: '入金済',
  partial: '一部入金',
  canceled: 'キャンセル',
};

export default function InvoiceList() {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [search, setSearch] = useState('');
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; id: string }>({ open: false, id: '' });
  const [paymentModal, setPaymentModal] = useState<{ open: boolean; invoice: Invoice | null }>({ open: false, invoice: null });
  const [outstanding, setOutstanding] = useState<any[]>([]);

  useEffect(() => {
    loadInvoices();
    loadOutstanding();
  }, [search]);

  const loadInvoices = async () => {
    const result = await window.electronAPI.invoke('invoice:list', { search });
    setInvoices(result);
  };

  const loadOutstanding = async () => {
    const result = await window.electronAPI.invoke('invoice:getOutstanding');
    setOutstanding(result);
  };

  const handleDelete = async () => {
    try {
      await window.electronAPI.invoke('invoice:delete', deleteDialog.id);
      setDeleteDialog({ open: false, id: '' });
      loadInvoices();
    } catch (error) {
      console.error('削除エラー:', error);
    }
  };

  const handlePrintPdf = async (id: string) => {
    try {
      const pdfPath = await window.electronAPI.invoke('invoice:printPdf', id);
      window.electronAPI.invoke('shell:openPath', pdfPath);
    } catch (error) {
      console.error('PDF出力エラー:', error);
    }
  };

  const handlePaymentComplete = () => {
    setPaymentModal({ open: false, invoice: null });
    loadInvoices();
    loadOutstanding();
  };

  const outstandingTotal = outstanding.reduce((sum, inv) => sum + (inv.total - inv.paid_amount), 0);

  const columns = [
    { id: 'doc_no', label: '請求No', minWidth: 120 },
    { id: 'customer_name', label: '顧客名', minWidth: 150 },
    { id: 'work_title', label: '件名', minWidth: 200 },
    { id: 'issue_date', label: '発行日', minWidth: 100 },
    { id: 'due_date', label: '支払期限', minWidth: 100 },
    {
      id: 'total',
      label: '請求額',
      minWidth: 100,
      render: (value: number) => `¥${value.toLocaleString()}`,
    },
    {
      id: 'paid_amount',
      label: '入金額',
      minWidth: 100,
      render: (value: number) => `¥${value.toLocaleString()}`,
    },
    {
      id: 'status',
      label: 'ステータス',
      minWidth: 100,
      render: (value: string) => (
        <Chip
          label={statusLabels[value] || value}
          size="small"
          sx={{ backgroundColor: statusColors[value] || '#9e9e9e', color: 'white' }}
        />
      ),
    },
    {
      id: 'actions',
      label: '操作',
      minWidth: 200,
      render: (_: any, row: Invoice) => (
        <Box>
          <IconButton size="small" onClick={() => navigate(`/invoice/edit/${row.id}`)}>
            <Edit />
          </IconButton>
          <IconButton size="small" onClick={() => handlePrintPdf(row.id)}>
            <PictureAsPdf />
          </IconButton>
          <IconButton size="small" onClick={() => setPaymentModal({ open: true, invoice: row })}>
            <Payment />
          </IconButton>
          <IconButton size="small" onClick={() => setDeleteDialog({ open: true, id: row.id })}>
            <Delete />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5">請求書一覧</Typography>
        <Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/invoice/create')}
          >
            注文請書から作成
          </Button>
        </Box>
      </Box>

      {/* 未回収サマリ */}
      {outstanding.length > 0 && (
        <Paper sx={{ p: 2, mb: 2, backgroundColor: '#fff3e0' }}>
          <Typography variant="h6" color="error">
            未回収請求: {outstanding.length}件 / 合計 ¥{outstandingTotal.toLocaleString()}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            支払期限を過ぎた請求書があります。早急な対応をお願いします。
          </Typography>
        </Paper>
      )}

      <TextField
        label="検索"
        variant="outlined"
        size="small"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2, width: 300 }}
      />

      <DataTable columns={columns} rows={invoices} />

      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, id: '' })}>
        <DialogTitle>削除確認</DialogTitle>
        <DialogContent>
          <Typography>
            この請求書を削除してもよろしいですか？
            {invoices.find(i => i.id === deleteDialog.id)?.status === 'issued' && (
              <Typography color="error" sx={{ mt: 1 }}>
                警告: 発行済みの請求書です。削除ではなくキャンセル処理を推奨します。
              </Typography>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, id: '' })}>キャンセル</Button>
          <Button onClick={handleDelete} color="error">削除</Button>
        </DialogActions>
      </Dialog>

      {paymentModal.open && paymentModal.invoice && (
        <PaymentModal
          invoice={paymentModal.invoice}
          onClose={() => setPaymentModal({ open: false, invoice: null })}
          onComplete={handlePaymentComplete}
        />
      )}
    </Box>
  );
}
